// src/components/modals/EditMarcaModal.jsx
import React, { useState, useEffect } from 'react';
import { FaTags, FaPalette, FaFont, FaQuoteLeft, FaSpinner, FaEdit } from 'react-icons/fa';
import Swal from 'sweetalert2';

const EditMarcaModal = ({ isOpen, onClose, onSubmit, onUploadLogotipo, marca, user }) => {
    // Estados para el formulario
    const [editMarca, setEditMarca] = useState({
        nombre: '',
        rubro: '',
        tiendas: [],
        clientes: [],
        pointscondition: '',
        puntos: 'PUNTOS',
        puntosTicket: 6,
        coloresCorporativos: [],
        tipografia: '',
        eslogan: ''
    });

    // Estados para el modal
    const [newColor, setNewColor] = useState('');
    const [selectedColor, setSelectedColor] = useState('#3B82F6');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    // Colores predefinidos para el color picker
    const predefinedColors = [
        '#EF4444', '#F97316', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899',
        '#06B6D4', '#84CC16', '#F43F5E', '#A855F7', '#14B8A6', '#FBBF24', '#22C55E'
    ];

    // Cargar datos de la marca cuando se abre el modal
    useEffect(() => {
        if (isOpen && marca) {
            setEditMarca({
                nombre: marca.nombre || '',
                rubro: marca.rubro || '',
                tiendas: marca.tiendas || [],
                clientes: marca.clientes || [],
                pointscondition: marca.pointscondition || 'Condición para sumar puntos',
                puntos: marca.puntos || 'PUNTOS',
                puntosTicket: marca.puntosTicket || 6,
                coloresCorporativos: marca.coloresCorporativos || [],
                tipografia: marca.tipografia || '',
                eslogan: marca.eslogan || ''
            });
            setHasChanges(false);
        }
    }, [isOpen, marca]);

    // Función para validar colores
    const validateColor = (color) => {
        // Crear un elemento temporal para validar el color
        const tempElement = document.createElement('div');
        tempElement.style.color = color;

        // Si el color es válido, el navegador lo aplicará
        const isValid = tempElement.style.color !== '';

        // Validaciones adicionales
        if (!isValid) return false;

        // Validar formato hexadecimal
        const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
        if (hexRegex.test(color)) return true;

        // Validar formato RGB/RGBA
        const rgbRegex = /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/;
        if (rgbRegex.test(color)) return true;

        // Validar formato HSL/HSLA
        const hslRegex = /^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%\s*(,\s*[\d.]+\s*)?\)$/;
        if (hslRegex.test(color)) return true;

        // Validar nombres de colores CSS válidos
        const validColorNames = [
            'red', 'green', 'blue', 'yellow', 'black', 'white', 'gray', 'grey',
            'orange', 'purple', 'pink', 'brown', 'cyan', 'magenta', 'lime',
            'navy', 'teal', 'olive', 'maroon', 'aqua', 'fuchsia', 'silver'
        ];
        if (validColorNames.includes(color.toLowerCase())) return true;

        return false;
    };

    // Función para normalizar colores a formato hexadecimal
    const normalizeColor = (color) => {
        // Si ya es hexadecimal, devolverlo
        if (color.startsWith('#')) {
            // Expandir colores de 3 dígitos a 6
            if (color.length === 4) {
                return '#' + color[1] + color[1] + color[2] + color[2] + color[3] + color[3];
            }
            return color;
        }

        // Crear un elemento temporal para convertir el color
        const tempElement = document.createElement('div');
        tempElement.style.color = color;
        document.body.appendChild(tempElement);

        // Obtener el color computado
        const computedColor = window.getComputedStyle(tempElement).color;
        document.body.removeChild(tempElement);

        // Convertir RGB a hexadecimal
        if (computedColor.startsWith('rgb')) {
            const rgb = computedColor.match(/\d+/g);
            if (rgb && rgb.length >= 3) {
                const r = parseInt(rgb[0]).toString(16).padStart(2, '0');
                const g = parseInt(rgb[1]).toString(16).padStart(2, '0');
                const b = parseInt(rgb[2]).toString(16).padStart(2, '0');
                return `#${r}${g}${b}`;
            }
        }

        return color; // Si no se puede convertir, devolver el original
    };

    // Handlers para el formulario
    const handleMarcaChange = (e) => {
        const { name, value } = e.target;
        setEditMarca((prev) => ({ ...prev, [name]: value }));

        // Solo marcar cambios para campos editables
        const editableFields = ['nombre', 'rubro', 'pointscondition', 'coloresCorporativos', 'tipografia', 'eslogan'];
        if (editableFields.includes(name)) {
            setHasChanges(true);
        }
    };

    const handleAddColor = () => {
        if (!newColor.trim()) {
            Swal.fire({
                title: 'Error',
                text: 'Por favor ingresa un color',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        // Validar el color
        if (!validateColor(newColor.trim())) {
            Swal.fire({
                title: 'Color Inválido',
                text: 'Por favor ingresa un color válido en formato hexadecimal (#RRGGBB), RGB, HSL o nombre de color CSS',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        // Normalizar el color
        const normalizedColor = normalizeColor(newColor.trim());

        // Verificar que no esté duplicado
        if (editMarca.coloresCorporativos.includes(normalizedColor)) {
            Swal.fire({
                title: 'Color Duplicado',
                text: 'Este color ya ha sido agregado',
                icon: 'warning',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        // Agregar el color
        setEditMarca(prev => ({
            ...prev,
            coloresCorporativos: [...prev.coloresCorporativos, normalizedColor]
        }));
        setNewColor('');
        setHasChanges(true);

        // Mostrar confirmación
        Swal.fire({
            title: 'Color Agregado',
            text: `Color ${normalizedColor} agregado correctamente`,
            icon: 'success',
            timer: 1500,
            showConfirmButton: false
        });
    };

    const handleRemoveColor = (index) => {
        setEditMarca(prev => ({
            ...prev,
            coloresCorporativos: prev.coloresCorporativos.filter((_, i) => i !== index)
        }));
        setHasChanges(true);
    };

    const handleColorPickerSelect = (color) => {
        setSelectedColor(color);
        setNewColor(color);
    };

    const handleCustomColorChange = (e) => {
        const color = e.target.value;
        setSelectedColor(color);
        setNewColor(color);
    };



    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Solo enviar los campos editables
            const marcaData = {
                nombre: editMarca.nombre,
                rubro: editMarca.rubro,
                pointscondition: editMarca.pointscondition,
                coloresCorporativos: editMarca.coloresCorporativos,
                tipografia: editMarca.tipografia,
                eslogan: editMarca.eslogan
            };

            // Actualizar la marca
            await onSubmit(marcaData, marca._id);

            Swal.fire({
                title: 'Éxito',
                text: 'Marca actualizada correctamente',
                icon: 'success',
                confirmButtonText: 'Aceptar',
            });

            // Cerrar el modal
            onClose();

        } catch (error) {
            console.error('Error updating marca:', error);

            Swal.fire({
                title: 'Error',
                text: 'No se pudo actualizar la marca. Por favor, intenta de nuevo.',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (hasChanges) {
            Swal.fire({
                title: '¿Deseas salir sin guardar?',
                text: 'Tienes cambios sin guardar que se perderán',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sí, salir',
                cancelButtonText: 'Cancelar'
            }).then((result) => {
                if (result.isConfirmed) {
                    // Limpiar el formulario al cerrar
                    setEditMarca({
                        nombre: '',
                        rubro: '',
                        tiendas: [],
                        clientes: [],
                        pointscondition: 'Condición para sumar puntos',
                        puntos: 'PUNTOS',
                        puntosTicket: 6,
                        coloresCorporativos: [],
                        tipografia: '',
                        eslogan: ''
                    });
                    setNewColor('');
                    setHasChanges(false);
                    onClose();
                }
            });
        } else {
            onClose();
        }
    };

    if (!isOpen || !marca) return null;

    return (
        <div className="fixed z-50 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
                {/* Fondo oscuro */}
                <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={handleClose}>
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                {/* Contenedor del modal */}
                <div className="bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-md sm:max-w-lg lg:max-w-4xl mx-auto max-h-[90vh] overflow-y-auto">
                    <div className="px-6 pt-6 pb-4">
                        {/* Header */}
                        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                                <FaEdit className="w-6 h-6 mr-2 text-blue-500" />
                                Editar Marca: {marca.nombre}
                            </h3>
                            <button
                                onClick={handleClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Formulario */}
                        <form onSubmit={handleSubmit} className="mt-6">
                            <div className="space-y-6">
                                {/* Información básica */}
                                <div>
                                    <h4 className="text-sm font-medium text-gray-700 mb-4 border-b pb-2 flex items-center">
                                        <FaTags className="mr-2 text-blue-500" />
                                        Información Básica
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Nombre de la Marca*
                                            </label>
                                            <input
                                                type="text"
                                                name="nombre"
                                                value={editMarca.nombre}
                                                onChange={handleMarcaChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                                placeholder="Ej. Mi Marca"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Rubro de la Marca*
                                            </label>
                                            <input
                                                type="text"
                                                name="rubro"
                                                value={editMarca.rubro}
                                                onChange={handleMarcaChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                                placeholder="Ej. Tecnología"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Configuración de Fidelización */}
                                <div>
                                    <h4 className="text-sm font-medium text-gray-700 mb-4 border-b pb-2 flex items-center">
                                        <FaTags className="mr-2 text-green-500" />
                                        Configuración de Fidelización
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Condición de Puntos
                                            </label>
                                            <input
                                                type="text"
                                                name="pointscondition"
                                                value={editMarca.pointscondition}
                                                onChange={handleMarcaChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                                placeholder="Condición para sumar puntos"
                                            />
                                        </div>
                                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                            <p className="text-xs text-gray-500 mb-2">
                                                <strong>Nota:</strong> Solo se puede modificar la condición de puntos.
                                            </p>
                                            <div className="space-y-2 text-sm">
                                                <div>
                                                    <span className="text-gray-500">Tipo de Puntos:</span>
                                                    <p className="font-medium text-gray-700">{editMarca.puntos}</p>
                                                </div>
                                                {editMarca.puntos === 'PUNTOS' && (
                                                    <div>
                                                        <span className="text-gray-500">Puntos por Ticket:</span>
                                                        <p className="font-medium text-gray-700">{editMarca.puntosTicket}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Identidad Visual */}
                                <div>
                                    <h4 className="text-sm font-medium text-gray-700 mb-4 border-b pb-2 flex items-center">
                                        <FaPalette className="mr-2 text-purple-500" />
                                        Identidad Visual
                                    </h4>

                                    {/* Colores Corporativos */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Colores Corporativos
                                        </label>

                                        {/* Colores seleccionados */}
                                        <div className="mb-4">
                                            <label className="block text-gray-600 text-sm mb-2">
                                                Colores seleccionados ({editMarca.coloresCorporativos.length}):
                                            </label>
                                            {editMarca.coloresCorporativos.length > 0 ? (
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    {editMarca.coloresCorporativos.map((color, index) => (
                                                        <div key={index} className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
                                                            <div
                                                                className="w-8 h-8 rounded-full border-2 border-gray-300 shadow-sm"
                                                                style={{ backgroundColor: color }}
                                                            />
                                                            <div className="flex-1 min-w-0">
                                                                <span className="text-sm font-mono text-gray-700 break-all">{color}</span>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveColor(index)}
                                                                className="text-red-500 hover:text-red-700 transition-colors p-1 rounded-full hover:bg-red-50"
                                                                title="Eliminar color"
                                                            >
                                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                                                    <FaPalette className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                                                    <p className="text-sm text-gray-500">No hay colores seleccionados</p>
                                                    <p className="text-xs text-gray-400">Usa el selector de abajo para agregar colores</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Color picker visual */}
                                        <div className="mb-4">
                                            <label className="block text-gray-600 text-sm mb-2">Seleccionar color:</label>

                                            {/* Selector de color HTML5 nativo */}
                                            <div className="flex items-center gap-3 mb-3">
                                                <input
                                                    type="color"
                                                    value={selectedColor}
                                                    onChange={handleCustomColorChange}
                                                    className="w-12 h-12 border-2 border-gray-300 rounded-lg cursor-pointer"
                                                    title="Seleccionar color personalizado"
                                                />
                                                <span className="text-sm text-gray-600">
                                                    Color seleccionado: <span className="font-medium">{selectedColor}</span>
                                                </span>
                                            </div>

                                            {/* Colores predefinidos */}
                                            <div className="mb-3">
                                                <label className="block text-gray-600 text-xs mb-2">Colores populares:</label>
                                                <div className="grid grid-cols-7 gap-2">
                                                    {predefinedColors.map((color) => (
                                                        <button
                                                            key={color}
                                                            type="button"
                                                            onClick={() => handleColorPickerSelect(color)}
                                                            className="w-8 h-8 rounded-full border-2 border-gray-300 hover:border-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                            style={{ backgroundColor: color }}
                                                            title={color}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Input manual de color */}
                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-gray-600 text-xs mb-2">O escribe un color personalizado:</label>
                                                <div className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        name="newColor"
                                                        value={newColor}
                                                        onChange={handleCustomColorChange}
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter') {
                                                                handleAddColor();
                                                            }
                                                        }}
                                                        placeholder="Ej: #ff0000, rgb(255,0,0), red, hsl(0,100%,50%)"
                                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={handleAddColor}
                                                        disabled={!newColor.trim()}
                                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
                                                    >
                                                        Agregar
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Información de formatos soportados */}
                                            <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                                                <p className="font-medium mb-1">Formatos soportados:</p>
                                                <ul className="space-y-1">
                                                    <li>• <strong>Hexadecimal:</strong> #ff0000, #f00</li>
                                                    <li>• <strong>RGB:</strong> rgb(255,0,0), rgba(255,0,0,0.5)</li>
                                                    <li>• <strong>HSL:</strong> hsl(0,100%,50%), hsla(0,100%,50%,0.5)</li>
                                                    <li>• <strong>Nombres CSS:</strong> red, blue, green, etc.</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tipografía */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                                            <FaFont className="mr-2" />
                                            Tipografía Principal
                                        </label>
                                        <div className="relative">
                                            <select
                                                name="tipografia"
                                                value={editMarca.tipografia}
                                                onChange={handleMarcaChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors appearance-none bg-white"
                                            >
                                                <option value="">Seleccionar tipografía</option>
                                                <option value="Arial">Arial</option>
                                                <option value="Helvetica">Helvetica</option>
                                                <option value="Times New Roman">Times New Roman</option>
                                                <option value="Georgia">Georgia</option>
                                                <option value="Verdana">Verdana</option>
                                                <option value="Roboto">Roboto</option>
                                                <option value="Open Sans">Open Sans</option>
                                                <option value="Lato">Lato</option>
                                                <option value="Montserrat">Montserrat</option>
                                                <option value="Poppins">Poppins</option>
                                                <option value="Inter">Inter</option>
                                                <option value="Source Sans Pro">Source Sans Pro</option>
                                                <option value="Ubuntu">Ubuntu</option>
                                                <option value="Nunito">Nunito</option>
                                                <option value="Playfair Display">Playfair Display</option>
                                            </select>
                                            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </div>
                                        </div>

                                        {/* Vista previa de la tipografía seleccionada */}
                                        {editMarca.tipografia && (
                                            <div className="mt-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                                                <p className="text-xs text-blue-600 mb-3 font-medium">✨ Vista previa de la tipografía</p>
                                                <div className="space-y-2">
                                                    <p
                                                        className="text-xl font-semibold text-gray-800"
                                                        style={{ fontFamily: editMarca.tipografia }}
                                                    >
                                                        {editMarca.nombre || 'Nombre de la Marca'}
                                                    </p>
                                                    <p
                                                        className="text-base text-gray-600"
                                                        style={{ fontFamily: editMarca.tipografia }}
                                                    >
                                                        {editMarca.eslogan || 'Tu eslogan aparecerá aquí con esta tipografía'}
                                                    </p>
                                                    <p className="text-xs text-blue-500 mt-2">
                                                        Fuente: {editMarca.tipografia}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Eslogan */}
                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                                            <FaQuoteLeft className="mr-2" />
                                            Eslogan o Tagline
                                        </label>
                                        <textarea
                                            name="eslogan"
                                            value={editMarca.eslogan}
                                            onChange={handleMarcaChange}
                                            rows={3}
                                            placeholder="Ej: Innovación que transforma..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
                                        />
                                    </div>


                                </div>
                            </div>

                            {/* Botones */}
                            <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting || !hasChanges}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors duration-200 font-medium flex items-center"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <FaSpinner className="w-5 h-5 mr-2 animate-spin" />
                                            Guardando...
                                        </>
                                    ) : (
                                        <>
                                            <FaEdit className="w-5 h-5 mr-2" />
                                            Guardar Cambios
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditMarcaModal; 